import { useLab, usePatient } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { labOrderFormSchema, type LabOrderFormValues } from '@schema/labOrder.schema'
import { LabOrderCreatePayload } from '@type/lab.type'
import { FormButton, Modal, SelectInputField, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { buildLabOrderPayload, newFreeTextOption, searchTests, type TestLineOption } from '@util/testCatalogue'
import { debounce } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import type { GroupBase } from 'react-select'
import { OptionTag, TestLineRows } from './TestLineRows'

type Props = {
  onClose: () => void
  patientId?: string
  encounterId?: string
}

type ExtendedLabOrderFormValues = LabOrderFormValues & { patient_id?: string }

const AddLabOrder = ({ onClose, patientId: patientIdProp, encounterId: encounterIdProp }: Props) => {
  const { patientId: patientIdParam, encounterId: encounterIdParam } = useParams<{
    patientId: string
    encounterId: string
  }>()
  const resolvedPatientId = patientIdProp ?? patientIdParam
  const resolvedEncounterId = encounterIdProp ?? encounterIdParam
  const methods = useForm<ExtendedLabOrderFormValues>({
    defaultValues: { lab_tests: [], note: '' },
    resolver: yupResolver(labOrderFormSchema)
  })
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting }
  } = methods

  const { createLab } = useLab()

  const { singlePatient } = usePatient({ id: resolvedPatientId })

  // patient search state + debounced handler
  const [patientSearch, setPatientSearch] = useState('')
  const debouncedSetPatientSearch = useCallback(
    debounce((q: string) => setPatientSearch(q), 400),
    []
  )
  const handlePatientSearch = (q: string) => debouncedSetPatientSearch(q)

  const { patients } = usePatient({ query: patientSearch, page: 1, limit: 50 })

  // if modal was opened with an existing patient, populate default value
  useEffect(() => {
    if (singlePatient) {
      setValue('patient_id', String(singlePatient.id))
    } else if (resolvedPatientId) {
      setValue('patient_id', String(resolvedPatientId))
    }
  }, [singlePatient, resolvedPatientId, setValue])

  // Facility list + shared catalogue (aliases such as "cbc" resolve server-side); free text still allowed.
  const loadLabTestOptions = useCallback((inputValue: string) => searchTests('lab', inputValue), [])

  const onSubmit = async (form: ExtendedLabOrderFormValues) => {
    const finalPatientId = (watch('patient_id') as string | undefined) ?? resolvedPatientId
    if (!finalPatientId) {
      showToast('Missing patient')
      return
    }

    const payload = buildLabOrderPayload(
      finalPatientId,
      form.lab_tests,
      form.note ?? '',
      resolvedEncounterId
    ) as LabOrderCreatePayload

    try {
      await createLab(payload)
      showToast('Lab order placed successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Order lab test">
        <form
          className="flex w-full min-w-0 max-w-[530px] flex-col gap-4 overflow-auto"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col gap-4 overflow-auto p-6">
            {!resolvedPatientId && (
              <SelectInputField
                dropProps={{ align: { left: 'left', right: 'right' }, height: '200px' }}
                label="Search patient"
                labelKey={({ attributes }) => `${attributes.first_name} ${attributes.last_name}`}
                name="patient_id"
                onSearch={handlePatientSearch}
                options={patients || []}
                placeholder="Search Patient"
                searchPlaceholder="eg John doe"
                valueKey={{ key: 'id', reduce: true }}
              />
            )}

            <Controller
              control={control}
              name="lab_tests"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Select<TestLineOption, true, GroupBase<TestLineOption>>
                    borderRadius={'15px'}
                    defaultOptions
                    error={error?.message}
                    formatOptionLabel={(option, { context }) => (
                      <span>
                        {option.label}
                        {context === 'menu' ? <OptionTag option={option} /> : null}
                      </span>
                    )}
                    getNewOptionData={(inputValue, optionLabel) => newFreeTextOption(inputValue, optionLabel)}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => String(o.value)}
                    isAsync
                    isCreatable
                    isMulti
                    label="Test"
                    loadOptions={loadLabTestOptions}
                    onChange={opts => onChange((opts as TestLineOption[] | null) ?? [])}
                    placeholder="Search tests (e.g. FBC, malaria) or type a name"
                    value={value as TestLineOption[]}
                  />
                  <TestLineRows kind="lab" lines={(value as TestLineOption[]) ?? []} onChange={onChange} />
                </>
              )}
            />

            <TextAreaField label="Note to lab" name="note" placeholder="Enter Note" />
          </div>
          <FormButton isLoading={isSubmitting} onClose={onClose} submit="Order" />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default AddLabOrder
